import React, { useState, useEffect, useRef } from 'react';
import { 
  Play, 
  Pause, 
  ChevronLeft, 
  ChevronRight, 
  Sparkles, 
  Cpu, 
  Globe, 
  Mail, 
  MessageSquare, 
  Share2, 
  TrendingUp, 
  Mic, 
  CreditCard, 
  ShieldCheck, 
  Compass,
  ArrowRight,
  Server,
  Lock,
  Check,
  Send,
  CheckCircle,
  Sliders,
  Settings,
  Users,
  Database,
  HelpCircle
} from 'lucide-react';

interface Scene {
  title: string;
  text: string;
  voiceover: string;
  visualDirection: string;
}

export interface GeneratedScenePack {
  guideId: string;
  videoTitle: string;
  introLine: string;
  scenes: Scene[];
  ctaEnding: string;
  durationTarget: string;
}

interface NexoraXGeneratedGuideVideoProps {
  guideId: string;
  title: string;
  scenePack: GeneratedScenePack;
  onCtaClick?: () => void;
  showToast?: (msg: string) => void;
}

// Maps categories/guide-ids to icons for specific visual branding
const getProductIcon = (guideId: string) => {
  const normalized = guideId.toLowerCase();
  if (normalized.includes('search')) return <Compass className="w-8 h-8 text-cyan-400" />;
  if (normalized.includes('hosting')) return <Globe className="w-8 h-8 text-indigo-400" />;
  if (normalized.includes('mail')) return <Mail className="w-8 h-8 text-blue-400" />;
  if (normalized.includes('chat')) return <MessageSquare className="w-8 h-8 text-emerald-400" />;
  if (normalized.includes('social')) return <Share2 className="w-8 h-8 text-pink-400" />;
  if (normalized.includes('ai-automation')) return <Cpu className="w-8 h-8 text-fuchsia-400" />;
  if (normalized.includes('voice')) return <Mic className="w-8 h-8 text-purple-400" />;
  if (normalized.includes('billing')) return <CreditCard className="w-8 h-8 text-amber-400" />;
  if (normalized.includes('privacy')) return <ShieldCheck className="w-8 h-8 text-emerald-500" />;
  return <Sparkles className="w-8 h-8 text-cyan-400" />;
};

// Maps categories/guide-ids to rich CSS gradient backgrounds for themed atmospheric visual stages
const getProductBackground = (guideId: string) => {
  const normalized = guideId.toLowerCase();
  if (normalized.includes('search')) {
    return 'from-cyan-950/40 via-blue-950/20 to-black';
  }
  if (normalized.includes('hosting')) {
    return 'from-indigo-950/40 via-slate-950/20 to-black';
  }
  if (normalized.includes('mail')) {
    return 'from-blue-950/40 via-indigo-950/20 to-black';
  }
  if (normalized.includes('chat')) {
    return 'from-emerald-950/40 via-teal-950/20 to-black';
  }
  if (normalized.includes('social')) {
    return 'from-pink-950/30 via-rose-950/15 to-black';
  }
  if (normalized.includes('ai-automation')) {
    return 'from-fuchsia-950/40 via-purple-950/20 to-black';
  }
  if (normalized.includes('voice')) {
    return 'from-purple-950/40 via-violet-950/20 to-black';
  }
  if (normalized.includes('billing')) {
    return 'from-amber-950/35 via-zinc-950/20 to-black';
  }
  if (normalized.includes('privacy')) {
    return 'from-emerald-950/35 via-slate-950/25 to-black';
  }
  return 'from-purple-950/20 via-blue-950/20 to-black';
};

export const NexoraXGeneratedGuideVideo: React.FC<NexoraXGeneratedGuideVideoProps> = ({
  guideId,
  title,
  scenePack,
  onCtaClick,
  showToast
}) => {
  const { scenes, introLine, ctaEnding, durationTarget } = scenePack;
  
  const [currentSceneIndex, setCurrentSceneIndex] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState<boolean>(true);
  const [sceneProgress, setSceneProgress] = useState<number>(0); // 0 to 100 representing elapsed time in current scene
  const [showCtaScreen, setShowCtaScreen] = useState<boolean>(false);
  
  const progressIntervalRef = useRef<any>(null);
  const SCENE_DURATION_MS = 6000; // Each scene gets 6 seconds to run under autoplay mode

  const renderVisualSimulation = () => {
    const normId = guideId.toLowerCase();
    
    // Welcome to NexoraX
    if (normId.includes('welcome')) {
      return (
        <div className="w-full h-full flex flex-col justify-between p-2.5 space-y-2 text-left font-mono text-[8px] leading-relaxed">
          <div className="flex items-center justify-between border-b border-white/10 pb-1.5">
            <span className="text-cyan-400 font-bold tracking-wider">NEXORAX CORE OS v0.8s</span>
            <span className="text-[7px] text-zinc-500 animate-pulse">● SECURED</span>
          </div>
          <div className="flex-1 flex flex-col justify-center items-center space-y-2 relative">
            <div className="relative w-12 h-12 flex items-center justify-center">
              <div className="absolute inset-0 rounded-full border border-cyan-500/30 animate-[spin_6s_linear_infinite]" />
              <div className="absolute inset-1 rounded-full border border-dashed border-purple-500/40 animate-[spin_10s_linear_infinite]" />
              <Sparkles className="w-5 h-5 text-cyan-400 animate-pulse" />
            </div>
            <div className="text-center">
              <div className="text-[8.5px] font-bold text-white tracking-widest">SYSTEM ONLINE</div>
              <div className="text-zinc-500 text-[6.5px]">Node 01: VANGUARD-CLOSER</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-1 bg-black/40 p-1 border border-white/5 rounded-lg text-[6.5px]">
            <div>GROSS: <span className="text-emerald-400">£2.82M</span></div>
            <div>LEVY: <span className="text-purple-400">1% ACTIVE</span></div>
            <div>COMPLY: <span className="text-amber-400">PASSED</span></div>
            <div>FUNNEL: <span className="text-cyan-400">15m-2m-8m</span></div>
          </div>
        </div>
      );
    }

    // Nexus Search
    if (normId.includes('search')) {
      return (
        <div className="w-full h-full flex flex-col justify-between p-2.5 space-y-1.5 text-left font-sans text-[8px] leading-normal">
          <div className="bg-slate-900 border border-cyan-500/40 rounded-lg p-1.5 flex items-center gap-1 shadow-[0_0_8px_rgba(6,182,212,0.15)]">
            <span className="text-cyan-400 text-[8.5px]">🔍</span>
            <span className="text-zinc-300 font-mono text-[8px] animate-[pulse_2s_infinite] truncate">
              {currentSceneIndex === 0 ? "best B2B suppliers London..." : "industry: tech sourcing Ldn..."}
            </span>
          </div>
          
          <div className="flex-1 flex flex-col gap-1 overflow-hidden justify-center pl-0.5">
            <div className={`p-1 bg-black/50 border rounded-lg flex justify-between items-center transition-all ${currentSceneIndex >= 1 ? 'border-cyan-500/35 bg-cyan-950/20 shadow-[0_0_8px_rgba(6,182,212,0.1)]' : 'border-white/5 opacity-80'}`}>
              <div>
                <div className="font-bold text-zinc-100">Elite Logistics Ltd</div>
                <div className="text-zinc-500 text-[6.5px]">London • B2B Wholesaler</div>
              </div>
              <span className="text-emerald-400 font-mono font-bold text-[7.5px]">98% Match</span>
            </div>
            <div className="p-1 bg-black/30 border border-white/5 rounded-lg flex justify-between items-center opacity-40">
              <div>
                <div className="font-bold text-zinc-400">Prime Sourcing Corp</div>
                <div className="text-zinc-500 text-[6.5px]">United Kingdom • Global</div>
              </div>
              <span className="text-zinc-400 font-mono font-bold text-[7.5px]">82% Match</span>
            </div>
          </div>

          {currentSceneIndex >= 2 && (
            <div className="bg-cyan-950/50 border border-cyan-500/25 p-1 rounded-lg font-mono text-[7px] text-cyan-300/90 flex items-center gap-1 animate-[fade-in_0.3s_ease] shadow-sm">
              <Sparkles className="w-2.5 h-2.5 text-cyan-400 shrink-0 animate-pulse" />
              <span>AI Synthesizer: 3 leads validated.</span>
            </div>
          )}
          
          {currentSceneIndex >= 4 && (
            <div className="bg-purple-950/50 border border-purple-500/25 px-1.5 py-1 rounded-lg text-center text-[7px] text-purple-300 font-mono animate-bounce mt-1.5 font-bold shadow">
              📤 Dispatched to Workspace Agent
            </div>
          )}
        </div>
      );
    }

    // Nexus Hosting
    if (normId.includes('hosting')) {
      return (
        <div className="w-full h-full flex flex-col justify-between p-2.5 text-left font-mono text-[7.5px] leading-relaxed">
          <div className="flex items-center justify-between border-b border-white/10 pb-1">
            <span className="text-indigo-400 font-bold uppercase">HOSTING NODE-06</span>
            <span className="text-emerald-400 animate-pulse font-sans">● LIVE</span>
          </div>
          
          <div className="flex-1 flex flex-col justify-center space-y-1 pl-0.5">
            <div className="flex items-center gap-1.5 p-1 bg-black/40 rounded-lg border border-white/5 shadow-inner">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping" />
              <span>Domain: <strong className="text-zinc-100 font-sans">nexorax.co.uk</strong></span>
            </div>
            <div className="flex items-center justify-between p-1 bg-black/40 rounded-lg border border-white/5 shadow-inner">
              <span className="flex items-center gap-1">🛡️ SSL cert:</span>
              <span className="text-emerald-400 font-bold font-sans">ACTIVE (TLS 1.3)</span>
            </div>
          </div>

          <div className="space-y-0.5 bg-black/40 p-1 border border-white/5 rounded-lg">
            <div className="flex items-center justify-between text-[6.5px]">
              <span className="text-zinc-400">Optimize assets stack:</span>
              <span className="text-cyan-400 font-bold">[✓]</span>
            </div>
            <div className="flex items-center justify-between text-[6.5px]">
              <span className="text-zinc-400">DNS zone connection:</span>
              <span className="text-cyan-400 font-bold">[✓]</span>
            </div>
            <div className="flex items-center justify-between text-[6.5px]">
              <span className="text-zinc-400">Container deploy live:</span>
              <span className={currentSceneIndex >= 3 ? "text-cyan-400 font-bold" : "text-zinc-500 animate-pulse font-bold"}>
                {currentSceneIndex >= 3 ? "[✓] READY" : "[...] COMPILING"}
              </span>
            </div>
          </div>
        </div>
      );
    }

    // Mail & Domains
    if (normId.includes('mail')) {
      return (
        <div className="w-full h-full flex flex-col justify-between p-2.5 text-left font-mono text-[7.5px] leading-relaxed">
          <div className="p-1 px-1.5 bg-indigo-950/40 border border-indigo-500/25 rounded-lg flex items-center justify-between mb-1 shadow-sm">
            <span>Domain: nexorax.co.uk</span>
            <span className="text-emerald-400 font-bold uppercase text-[6px] font-sans">✓ Verified Secure</span>
          </div>

          <div className="flex-1 flex flex-col justify-center space-y-1">
            <div className="p-1 bg-black/40 border border-white/5 rounded-lg flex justify-between items-center shadow-inner">
              <span className="text-zinc-300">✉ info@nexorax.co.uk</span>
              <span className="text-emerald-400 font-bold text-[6px] font-sans bg-emerald-950/30 px-1 rounded">SPF✓ DKIM✓</span>
            </div>
            <div className="p-1 bg-black/40 border border-white/5 rounded-lg flex justify-between items-center opacity-70 shadow-inner">
              <span className="text-zinc-300">✉ support@nexorax.co.uk</span>
              <span className="text-emerald-400 font-bold text-[6px] font-sans bg-emerald-950/30 px-1 rounded">SPF✓ DKIM✓</span>
            </div>
          </div>

          <div className="bg-black/50 p-1 border border-white/5 rounded-lg font-sans text-[6.5px] space-y-0.5">
            <div className="flex justify-between text-zinc-400">
              <span className="font-mono">MX mail.nexorax.co.uk:</span>
              <span className="text-cyan-400 font-mono">10</span>
            </div>
            <div className="flex justify-between text-zinc-400">
              <span className="font-mono">TXT v=spf1 include:mail:</span>
              <span className="text-cyan-400 font-mono">Matched</span>
            </div>
            <div className="flex justify-between text-zinc-400">
              <span className="font-mono">DMARC p=reject:</span>
              <span className={currentSceneIndex >= 3 ? "text-cyan-400 font-mono font-bold" : "text-amber-500 font-mono animate-pulse font-bold"}>
                {currentSceneIndex >= 3 ? "Enforced" : "Configuring"}
              </span>
            </div>
          </div>
        </div>
      );
    }

    // Nexus Chat
    if (normId.includes('chat')) {
      return (
        <div className="w-full h-full flex flex-col justify-between p-2.5 text-left font-sans text-[7.5px] space-y-1.5 leading-normal">
          <div className="flex-1 flex flex-col justify-end space-y-1 bg-black/40 p-1.5 border border-white/5 rounded-lg overflow-hidden shadow-inner">
            <div className="bg-zinc-800/60 p-1 rounded-lg max-w-[85%] self-start text-zinc-300">
              Hi, how does the platform connect?
            </div>
            <div className="bg-cyan-950/60 p-1 rounded-lg max-w-[85%] self-end text-cyan-200 border border-cyan-500/15">
              <span className="text-[6px] text-cyan-400 block font-mono font-bold uppercase tracking-wider mb-0.5">NexoraX Agent</span>
              {currentSceneIndex >= 1 ? "We use a direct-billing, unified API node strategy." : "Typing..."}
            </div>
          </div>

          {currentSceneIndex >= 2 && (
            <div className="bg-black/70 border border-emerald-500/25 p-1 rounded-lg font-mono text-[6.8px] text-emerald-400 flex flex-col animate-[fade-in_0.3s_ease] shadow-md">
              <div className="font-bold border-b border-white/10 pb-0.5 mb-0.5 text-[6px] uppercase tracking-widest text-emerald-500">CLIENT LEAD SUBMITTED [✓]</div>
              <div>Name: Siddique Mahmood</div>
              <div>Email: siddique.mahmood@gmail.com</div>
            </div>
          )}

          {currentSceneIndex >= 4 && (
            <div className="flex gap-1 justify-center mt-0.5 animate-pulse">
              <span className="px-1.5 py-0.5 rounded-md bg-emerald-950/80 border border-emerald-500/35 text-emerald-300 text-[6.5px] font-mono font-bold shadow-sm">
                HUMAN APPROVED
              </span>
            </div>
          )}
        </div>
      );
    }

    // Social Automation
    if (normId.includes('social')) {
      return (
        <div className="w-full h-full flex flex-col justify-between p-2.5 text-left font-sans text-[7.5px] space-y-1.5 leading-snug">
          <div className="bg-black/40 p-1.5 border border-white/5 rounded-lg space-y-1 shadow-inner">
            <span className="font-mono text-[7px] uppercase tracking-wider text-pink-400 font-extrabold block">Campaign Template Platform Preview</span>
            <p className="text-zinc-200 italic leading-snug">
              {currentSceneIndex >= 1 
                ? "🚀 Unifying enterprise scale communications with NexoraX! Direct-billing active. #AI #SaaS" 
                : "Awaiting platform caption queuing instruction sequence..."
              }
            </p>
          </div>

          <div className="grid grid-cols-3 gap-1 text-center font-mono">
            <div className="p-1 bg-black/60 border border-white/5 rounded-lg shadow-sm">
              <span className="text-[10px] block font-sans">𝕏</span>
              <span className="text-[5.5px] text-emerald-400 font-extrabold uppercase">QUEUED</span>
            </div>
            <div className="p-1 bg-black/60 border border-white/5 rounded-lg shadow-sm">
              <span className="text-[10px] block font-sans">in</span>
              <span className="text-[5.5px] text-emerald-400 font-extrabold uppercase">QUEUED</span>
            </div>
            <div className="p-1 bg-black/60 border border-white/5 rounded-lg shadow-sm">
              <span className="text-[10px] block font-sans">🧵</span>
              <span className="text-[5.5px] text-zinc-500 font-bold uppercase block">STAGED</span>
            </div>
          </div>

          <div className="bg-pink-950/45 border border-pink-500/25 px-1 py-1 rounded-lg text-center text-[7px] font-mono text-pink-300 font-bold shadow animate-pulse">
            {currentSceneIndex >= 3 ? "Campaign Pack Auto-Publish Synced" : "Preparing Platform Caption Drafts"}
          </div>
        </div>
      );
    }

    // AI Automation
    if (normId.includes('ai-automation') || normId.includes('automation-guide')) {
      return (
        <div className="w-full h-full flex flex-col justify-between p-2.5 text-left font-mono text-[7.5px] space-y-1 leading-normal">
          <div className="flex items-center justify-between border-b border-white/10 pb-1">
            <span className="text-fuchsia-400 font-extrabold uppercase tracking-wide">AUTOMATION GRAPH CONSOLE</span>
            <span className="text-emerald-400 animate-pulse font-sans">● ONLINE</span>
          </div>

          <div className="flex-1 flex flex-col justify-center space-y-1 relative pl-0.5">
            {/* Visual connector lines */}
            <div className="absolute left-[9px] top-2.5 bottom-2.5 w-0.5 bg-gradient-to-b from-cyan-500 to-fuchsia-500 pointer-events-none opacity-40" />
            
            <div className={`flex items-center gap-1.5 pl-3.5 relative transition-all ${currentSceneIndex === 0 ? 'scale-105' : 'opacity-50'}`}>
              <div className="absolute left-[6px] w-[6px] h-[6px] rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.7)]" />
              <div className="bg-black/40 border border-cyan-500/35 p-1 rounded-lg flex items-center gap-1 w-full text-[6.5px]">
                <span>⚡ TRIGGER: Form Input Received</span>
              </div>
            </div>

            <div className={`flex items-center gap-1.5 pl-3.5 relative transition-all ${currentSceneIndex === 1 ? 'scale-105' : 'opacity-50'}`}>
              <div className="absolute left-[6px] w-[6px] h-[6px] rounded-full bg-fuchsia-400 shadow-[0_0_8px_rgba(240,82,244,0.7)] animate-pulse" />
              <div className="bg-black/40 border border-fuchsia-500/25 p-1 rounded-lg flex items-center gap-1 w-full text-[6.5px]">
                <Cpu className="w-2 h-2 text-fuchsia-400 shrink-0" />
                <span>⚙️ PROCESSOR: AI Parsing Context</span>
              </div>
            </div>

            <div className={`flex items-center gap-1.5 pl-3.5 relative transition-all ${currentSceneIndex >= 2 ? 'scale-105' : 'opacity-50'}`}>
              <div className="absolute left-[6px] w-[6px] h-[6px] rounded-full bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.7)] animate-ping" />
              <div className="bg-black/40 border border-amber-500/25 p-1 rounded-lg flex items-center gap-1 w-full text-[6.5px]">
                <span>🛡️ RULE: Principal Secretary Match</span>
              </div>
            </div>
          </div>

          <div className="text-[6.5px] text-zinc-500 text-center uppercase tracking-widest font-extrabold bg-[#04081c] py-0.5 rounded">
            Tracing Stream: active_lead_matched_verification
          </div>
        </div>
      );
    }

    // Voice Matrix
    if (normId.includes('voice')) {
      return (
        <div className="w-full h-full flex flex-col justify-between p-2.5 text-left font-mono text-[7.5px] space-y-1.5 leading-normal">
          <div className="flex items-center justify-between border-b border-white/10 pb-1">
            <span className="text-purple-400 font-bold uppercase">VOICE MATRIX DISPATCHER</span>
            <span className="text-purple-300 animate-pulse font-sans">● LIVE CALL</span>
          </div>

          {/* Animated Waveform Bars */}
          <div className="flex-1 flex justify-center items-center gap-1 p-1 bg-black/40 border border-white/5 rounded-lg h-20 shadow-inner">
            {[1, 2, 3, 4, 3, 2, 5, 1, 4, 3, 2, 4, 1, 3, 2].map((val, idx) => {
              const scale = val * 5.2;
              const speed = (idx % 3 === 0) ? '1.2s' : (idx % 2 === 0) ? '1.8s' : '0.9s';
              return (
                <div 
                  key={idx} 
                  className="w-[2.5px] bg-gradient-to-t from-purple-500 via-violet-400 to-cyan-405 rounded-full"
                  style={{ 
                    height: `${scale + 6}px`,
                    animation: `pulse ${speed} ease-in-out infinite alternate`
                  }} 
                />
              );
            })}
          </div>

          <div className="bg-black/50 p-1 border border-white/5 rounded-lg text-[6.8px] text-zinc-300 italic min-h-[22px] flex items-center">
            {currentSceneIndex >= 1 
              ? "\"Thanks for calling NexoraX, directing to our Secure Invoice Register database...\"" 
              : "Voice Matrix active. Waiting for call trace..."
            }
          </div>
        </div>
      );
    }

    // Billing
    if (normId.includes('billing')) {
      return (
        <div className="w-full h-full flex flex-col justify-between p-2.5 text-left font-mono text-[7.5px] space-y-1.5 leading-normal">
          <div className="grid grid-cols-2 gap-1.5">
            <div className="p-1 bg-black/40 border border-white/5 rounded-lg flex flex-col justify-between shadow-sm">
              <span className="text-zinc-500 text-[6px]">SEARCH CREDITS</span>
              <strong className="text-cyan-400 text-[10px] mt-0.5">482 / 500</strong>
            </div>
            <div className="p-1 bg-black/40 border border-white/5 rounded-lg flex flex-col justify-between shadow-sm">
              <span className="text-zinc-500 text-[6px]">LICENSE LEVEL</span>
              <strong className="text-purple-400 text-[8.5px] mt-0.5 font-sans font-extrabold uppercase">VIP Lifetime</strong>
            </div>
          </div>

          <div className="flex-1 bg-black/50 p-1.5 rounded-lg border border-white/5 flex flex-col justify-center space-y-1 overflow-hidden shadow-inner">
            <div className="border-b border-white/5 flex justify-between text-[6.2px] pb-0.5 text-zinc-500">
              <span>LEDGER REFERENCE</span>
              <span>STATUS</span>
            </div>
            <div className="flex justify-between items-center text-[7.2px]">
              <span className="text-zinc-300 truncate max-w-[80px]">INV-2026-887 (SC887095)</span>
              <span className="text-emerald-400 uppercase font-bold text-[6px] bg-emerald-950/40 px-1 rounded-md border border-emerald-500/25 shrink-0">RECONCILED</span>
            </div>
          </div>

          {currentSceneIndex >= 4 ? (
            <div className="bg-amber-950/40 border border-amber-500/30 px-1.5 py-1 rounded-lg text-center text-[7px] text-amber-400 animate-bounce flex items-center justify-center gap-1 font-sans font-bold shadow-sm">
              💾 accountant_export.csv ready
            </div>
          ) : (
            <div className="text-zinc-550 text-[6px] text-center italic text-zinc-550">
              * Wednesday deadline synced to CloudSync-02
            </div>
          )}
        </div>
      );
    }

    // Privacy & Consent
    if (normId.includes('privacy')) {
      return (
        <div className="w-full h-full flex flex-col justify-between p-2.5 text-left font-sans text-[7.5px] space-y-1 leading-normal">
          <div className="bg-black/60 border border-emerald-500/25 p-1.5 rounded-lg shadow-md space-y-1 text-[6.8px]">
            <div className="flex justify-between items-center border-b border-white/10 pb-0.5 mb-0.5">
              <span className="font-mono text-[6.5px] text-emerald-400 uppercase font-bold">🛡️ Cookie Consent Regulations</span>
              <span className="text-[5.5px] text-zinc-400 bg-white/5 px-1 rounded">GDPR</span>
            </div>
            <div className="flex justify-between">
              <span>System Essential Files:</span>
              <span className="text-emerald-400 uppercase font-mono font-extrabold text-[6px]">ALWAYS ACTIVE</span>
            </div>
            <div className="flex justify-between items-center">
              <span>Performance Analytics Nodes:</span>
              <span className="text-[6px] px-1 bg-emerald-950/40 border border-emerald-500/20 text-emerald-400 rounded font-mono font-bold">ENABLED</span>
            </div>
            <div className="flex justify-between items-center">
              <span>Marketing Campaign Cookies:</span>
              <span className="text-[6px] px-1 bg-red-950/40 border border-red-500/20 text-red-400 rounded font-mono font-bold font-mono">DISABLED</span>
            </div>
          </div>

          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-black/30 border border-white/5 p-1 rounded-lg text-[6.5px] flex items-center gap-1 font-mono text-zinc-400">
              <Database className="w-2.5 h-2.5 text-indigo-400 shrink-0 animate-pulse" />
              <span>Consent token hashed to Secure Library</span>
            </div>
          </div>

          <div className="bg-[#030614] border-t border-white/5 pt-0.5 text-center font-mono text-[6.5px] text-zinc-500">
            nexorax.co.uk/privacy-policy-v1
          </div>
        </div>
      );
    }

    // Dynamic Fallback
    return (
      <div className="z-10 relative flex flex-col items-center text-center space-y-2.5">
        <div className="p-3 bg-cyan-950/20 border border-cyan-500/25 rounded-xl shadow-[0_0_15px_rgba(34,211,238,0.15)] animate-bounce duration-3000">
          {getProductIcon(guideId)}
        </div>
        <div className="space-y-1">
          <div className="text-[10px] font-mono text-cyan-400 uppercase tracking-wider">
            Scene {currentSceneIndex + 1} of {scenes.length}
          </div>
          <div className="text-[8px] text-zinc-400 max-w-[120px] font-sans truncate" title={activeScene.visualDirection}>
            {activeScene.visualDirection}
          </div>
        </div>
      </div>
    );
  };

  // Reset states when changing guide or initializing
  useEffect(() => {
    setCurrentSceneIndex(0);
    setSceneProgress(0);
    setShowCtaScreen(false);
    setIsPlaying(true);
  }, [guideId]);

  // Handle Autoplay Scene Progression & Progress bar counting
  useEffect(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    if (!isPlaying) return;

    if (showCtaScreen) return;

    const intervalStep = 100; // Count progress every 100ms
    const increment = (intervalStep / SCENE_DURATION_MS) * 100;

    progressIntervalRef.current = setInterval(() => {
      setSceneProgress((prev) => {
        if (prev >= 100) {
          // Time to transition to next scene or show final CTA ending screen
          if (currentSceneIndex < scenes.length - 1) {
            setCurrentSceneIndex((idx) => idx + 1);
            return 0; // Reset progress for the next scene
          } else {
            // Reached the end! Present CTA ending slate
            setShowCtaScreen(true);
            setIsPlaying(false);
            if (showToast) {
              showToast("✨ Generated demonstration completed successfully.");
            }
            return 100;
          }
        }
        return prev + increment;
      });
    }, intervalStep);

    return () => {
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
    };
  }, [isPlaying, currentSceneIndex, showCtaScreen, scenes.length, guideId]);

  const handleNext = () => {
    if (showCtaScreen) return;
    setSceneProgress(0);
    if (currentSceneIndex < scenes.length - 1) {
      setCurrentSceneIndex((idx) => idx + 1);
    } else {
      setShowCtaScreen(true);
      setIsPlaying(false);
    }
  };

  const handlePrev = () => {
    setSceneProgress(0);
    if (showCtaScreen) {
      setShowCtaScreen(false);
      setCurrentSceneIndex(scenes.length - 1);
      setIsPlaying(true);
    } else if (currentSceneIndex > 0) {
      setCurrentSceneIndex((idx) => idx - 1);
    }
  };

  const togglePlay = () => {
    if (showCtaScreen) {
      // Replay video from start
      setShowCtaScreen(false);
      setCurrentSceneIndex(0);
      setSceneProgress(0);
      setIsPlaying(true);
      if (showToast) {
        showToast("Relaunching generated session.");
      }
    } else {
      setIsPlaying((p) => !p);
    }
  };

  const activeScene = scenes[currentSceneIndex] || {
    title: "Intro",
    text: introLine,
    voiceover: introLine,
    visualDirection: "Continuous orbital flows of the core."
  };

  return (
    <div 
      className={`relative w-full rounded-2xl overflow-hidden border border-cyan-500/20 bg-gradient-to-b ${getProductBackground(guideId)} flex flex-col justify-between shadow-[0_0_40px_rgba(34,211,238,0.06)]`}
      style={{ aspectRatio: '16/9' }}
      id={`nexorax-generated-player-${guideId}`}
    >
      {/* Absolute futuristic alignment grids in background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none opacity-40"></div>
      
      {/* Sleek Header Indicator */}
      <div className="relative z-10 p-3 bg-gradient-to-b from-black/80 to-transparent flex items-center justify-between border-b border-white/5">
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse"></span>
          <span className="text-[9px] font-mono tracking-[0.2em] font-bold text-cyan-400 uppercase">
            NEXORAX GENERATED DEMO
          </span>
        </div>
        <div className="flex items-center gap-1.5 font-mono text-[8px] bg-cyan-950/40 text-cyan-300 border border-cyan-500/15 px-1.5 py-0.5 rounded">
          <span>Target: {durationTarget}</span>
        </div>
      </div>

      {/* Main Content Showcase Arena */}
      <div className="flex-1 relative flex items-center justify-center p-4">
        {!showCtaScreen ? (
          <div className="w-full h-full flex flex-col md:flex-row items-center gap-6 justify-between text-left">
            
            {/* Left Column: Kinetic Themed Visualization Graphic Block */}
            <div className="w-2/5 hidden md:flex flex-col items-center justify-center relative aspect-square max-h-[160px] bg-black/40 border border-white/5 rounded-xl p-4 overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500/5 via-transparent to-purple-500/5 pointer-events-none"></div>
              
              {/* Spinning particle or radial rings representing active step data flow */}
              <div className="absolute w-[110%] h-[110%] border border-dashed border-cyan-400/5 rounded-full animate-[spin_40s_linear_infinite]"></div>
              <div className="absolute w-[80%] h-[80%] border border-dashed border-purple-400/10 rounded-full animate-[spin_20s_linear_infinite]"></div>
              
              {renderVisualSimulation()}
            </div>

            {/* Right Column: Active Scene Detail Presentation block */}
            <div className="flex-1 flex flex-col justify-center space-y-3 min-w-0">
              <div className="space-y-1.5">
                {/* Mini tag step */}
                <span className="inline-block text-[8px] font-mono uppercase bg-purple-950/40 text-purple-400 border border-purple-500/20 px-1.5 py-0.5 rounded-md font-bold">
                  STEP 0{currentSceneIndex + 1}
                </span>
                
                {/* Scene Heading */}
                <h3 className="text-sm font-sans font-bold text-white tracking-tight animate-fade-in truncate">
                  {activeScene.title}
                </h3>
              </div>
              
              {/* On-Screen Caption featuring Gold highlights */}
              <div className="bg-black/40 border border-cyan-500/10 rounded-xl p-3 min-h-[56px] relative flex items-center shadow-[0_4px_12px_rgba(0,0,0,0.5)]">
                <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-cyan-400 rounded-l-xl"></div>
                <p className="text-xs font-sans font-semibold text-amber-300/90 leading-relaxed pl-2">
                  "{activeScene.text}"
                </p>
              </div>

              {/* Synthesized Simulated Audio/Voiceover transcription box */}
              <div className="bg-[#090f23]/60 border border-white/5 rounded-lg p-2 flex items-start gap-2">
                <div className="p-1 rounded bg-purple-950/30 text-purple-300 flex-shrink-0 mt-0.5 animate-pulse">
                  <Mic className="w-3 h-3" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="block text-[7.5px] font-mono text-zinc-500 uppercase tracking-widest leading-none mb-0.5">Voiceover Audio Transcription</span>
                  <p className="text-[9.5px] font-mono text-zinc-300 leading-normal truncate" title={activeScene.voiceover}>
                    {activeScene.voiceover}
                  </p>
                </div>
              </div>

            </div>

          </div>
        ) : (
          /* Staggering Cinematic Final Call-to-Action Slate */
          <div className="w-full h-full flex flex-col items-center justify-center text-center max-w-md mx-auto space-y-4 animate-[fade-in_0.5s_ease-out]">
            <div className="p-4 rounded-full bg-cyan-950/20 border border-cyan-400/25 shadow-[0_0_30px_rgba(34,211,238,0.15)] animate-pulse">
              {getProductIcon(guideId)}
            </div>
            
            <div className="space-y-1">
              <span className="text-[9px] font-mono text-cyan-400 uppercase tracking-[0.3em] font-semibold">FLOW COMPLETED</span>
              <h3 className="text-base font-sans font-bold text-white tracking-tight leading-snug">
                Ready to Deploy in Workspace?
              </h3>
              <p className="text-[10px] text-zinc-400 max-w-xs mx-auto leading-relaxed">
                Connect this automated flow blueprint to your active workspace module and begin managing tasks.
              </p>
            </div>

            <button
              onClick={onCtaClick}
              className="px-5 py-2 rounded-full font-mono text-xs font-bold text-black bg-gradient-to-r from-cyan-400 via-teal-400 to-emerald-400 hover:from-cyan-300 hover:to-emerald-300 transition-all duration-300 shadow-[0_0_20px_rgba(34,211,238,0.3)] hover:shadow-[0_0_25px_rgba(34,211,238,0.55)] cursor-pointer flex items-center gap-1.5 focus:outline-none ring-2 ring-cyan-400/25 active:scale-95"
            >
              <span>{ctaEnding || "Initialize Workspace Module"}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Controller Controls deck overlay */}
      <div className="relative z-10 px-4 py-2.5 bg-gradient-to-t from-black/95 to-black/30 flex flex-col gap-2 border-t border-white/5">
        
        {/* Progress Bar Timeline Container */}
        <div className="flex items-center gap-2">
          <span className="text-[8px] font-mono text-cyan-400 min-w-[32px] text-left">
            {showCtaScreen ? durationTarget : `SEC 0${currentSceneIndex + 1}`}
          </span>
          <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden relative">
            {/* Global background scene lines grid */}
            <div className="absolute inset-0 flex justify-between">
              {scenes.map((_, i) => (
                <div key={i} className="h-full w-[1px] bg-white/10"></div>
              ))}
            </div>
            {/* Real elapsed highlight indicator line */}
            <div 
              className="h-full bg-gradient-to-r from-cyan-400 to-purple-500 rounded-full transition-all duration-100 ease-linear"
              style={{ 
                width: showCtaScreen 
                  ? '100%' 
                  : `${((currentSceneIndex / scenes.length) * 100) + (sceneProgress / scenes.length)}%` 
              }}
            ></div>
          </div>
          <span className="text-[8px] font-mono text-zinc-500 min-w-[32px] text-right">
            {durationTarget}
          </span>
        </div>

        {/* Buttons Controls Section */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            
            {/* Run / Stop Autoplay simulation */}
            <button 
              onClick={togglePlay}
              className="p-1 rounded hover:bg-white/10 text-white hover:text-cyan-400 cursor-pointer transition-all focus:outline-none flex items-center justify-center"
              title={showCtaScreen ? "Replay Demo" : isPlaying ? "Pause Demo" : "Resume Auto Progression"}
            >
              {showCtaScreen ? (
                <span className="text-[8px] font-mono font-bold text-cyan-400 uppercase bg-cyan-950/30 px-1.5 py-0.5 rounded border border-cyan-500/20 flex items-center gap-1">
                  <Play className="w-2.5 h-2.5 fill-cyan-400 text-cyan-400" /> REPLAY
                </span>
              ) : isPlaying ? (
                <Pause className="w-3.5 h-3.5 text-cyan-400" />
              ) : (
                <Play className="w-3.5 h-3.5 fill-white text-white hover:text-cyan-400" />
              )}
            </button>

            {/* Navigation sequence controllers */}
            <div className="flex items-center gap-0.5">
              <button 
                onClick={handlePrev}
                disabled={currentSceneIndex === 0 && !showCtaScreen}
                className="p-1 rounded hover:bg-white/10 text-zinc-400 hover:text-white disabled:opacity-20 cursor-pointer disabled:cursor-not-allowed transition-all focus:outline-none"
                title="Previous Scene"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <button 
                onClick={handleNext}
                disabled={showCtaScreen}
                className="p-1 rounded hover:bg-white/10 text-zinc-400 hover:text-white disabled:opacity-20 cursor-pointer disabled:cursor-not-allowed transition-all focus:outline-none"
                title="Next Scene"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Status light */}
            <span className="hidden sm:inline-block text-[7.5px] font-mono text-zinc-500 bg-white/5 border border-white/5 pl-1.5 pr-2 py-0.5 rounded uppercase">
              Mode: <strong className="text-purple-400 font-bold">Generative AI Vector</strong>
            </span>

          </div>

          <div className="text-[9px] font-mono text-zinc-400 truncate max-w-[150px] sm:max-w-none">
            {showCtaScreen ? "Demonstration Ready" : `Active Scene: ${activeScene.title}`}
          </div>
        </div>

      </div>
    </div>
  );
};
